import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { environment } from '../../../environments/environment';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import {
  LocalizationImportPreviewResponse,
  LocalizationResourceDetailResponse,
  LocalizationResourceEntry,
  LocalizationResourceFileSummary,
  PlatformAdminService
} from './platform-admin.service';

@Component({
  standalone: true,
  selector: 'ain-platform-localization-resource-manager',
  imports: [AinPageComponent, FormsModule, TableModule, ButtonModule, InputTextModule, SelectModule, DialogModule],
  template: `
    <ain-page
      [eyebrow]="t('platform.eyebrow')"
      [title]="t('platform.localizationResources.title')"
      [description]="t('platform.localizationResources.description')">
      <div class="toolbar">
        <label>
          <span>{{ t('platform.localizationResources.scope') }}</span>
          <p-select
            [options]="scopeOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="selectedScope"
            (ngModelChange)="onScopeOrCultureChanged()"
            appendTo="body" />
        </label>
        <label>
          <span>{{ t('platform.localizationResources.culture') }}</span>
          <p-select
            [options]="cultureOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="selectedCulture"
            (ngModelChange)="onScopeOrCultureChanged()"
            appendTo="body" />
        </label>
        <label class="search-field">
          <span>{{ t('platform.localizationResources.search') }}</span>
          <input pInputText [(ngModel)]="searchTerm" [placeholder]="t('platform.localizationResources.searchPlaceholder')" />
        </label>
        <div class="toolbar-actions">
          <button pButton type="button" severity="secondary" [label]="t('platform.localizationResources.refresh')" (click)="loadEntries()"></button>
          <button pButton type="button" severity="secondary" [label]="t('platform.localizationResources.export')" (click)="exportJson()" [disabled]="!selectedScope || !selectedCulture"></button>
          <button pButton type="button" [label]="t('platform.localizationResources.import')" (click)="openImportPicker()" [disabled]="!selectedScope || !selectedCulture"></button>
          <input #importInput type="file" accept="application/json,.json" class="hidden-file-input" (change)="onImportFileSelected($event)" />
        </div>
      </div>

      @if (resourceMode()) {
        <p class="meta">
          {{ t('platform.localizationResources.mode') }}:
          {{ resourceMode() === 'Nested' ? t('platform.localizationResources.modeNested') : t('platform.localizationResources.modeFlat') }}
          · {{ filteredEntries().length }} / {{ entries().length }}
        </p>
      }

      <p-table [value]="filteredEntries()" [paginator]="true" [rows]="25" [rowsPerPageOptions]="[25, 50, 100]">
        <ng-template pTemplate="header">
          <tr>
            <th>{{ t('platform.localizationResources.key') }}</th>
            <th>{{ t('platform.localizationResources.value') }}</th>
            <th>{{ t('columns.actions') }}</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-row>
          <tr>
            <td><code>{{ row.key }}</code></td>
            <td class="value-cell">{{ row.value }}</td>
            <td>
              <button pButton type="button" text="true" size="small" [label]="t('common.edit')" (click)="openEditDialog(row)"></button>
            </td>
          </tr>
        </ng-template>
      </p-table>

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }
    </ain-page>

    <p-dialog
      [header]="t('platform.localizationResources.editTitle')"
      [visible]="editDialogOpen()"
      [modal]="true"
      [style]="{ width: '42rem', maxWidth: '96vw' }"
      (visibleChange)="editDialogOpen.set($event)">
      <div class="dialog-body">
        <p><strong>{{ t('platform.localizationResources.key') }}:</strong> <code>{{ editingEntry()?.key }}</code></p>
        <label>
          <span>{{ t('platform.localizationResources.value') }}</span>
          <textarea pInputText rows="4" [(ngModel)]="editValue"></textarea>
        </label>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="editDialogOpen.set(false)"></button>
        <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="saveEntry()"></button>
      </ng-template>
    </p-dialog>

    <p-dialog
      [header]="t('platform.localizationResources.importPreviewTitle')"
      [visible]="importDialogOpen()"
      [modal]="true"
      [style]="{ width: '56rem', maxWidth: '96vw' }"
      (visibleChange)="importDialogOpen.set($event)">
      @if (importPreview()) {
        <div class="dialog-body">
          @if (!importPreview()!.isValid) {
            <p class="message error">{{ t('platform.localizationResources.importInvalid') }}</p>
            <ul>
              @for (error of importPreview()!.errors; track error) {
                <li>{{ error }}</li>
              }
            </ul>
          } @else {
            <p>{{ t('platform.localizationResources.importSummary', { current: importPreview()!.currentKeyCount, incoming: importPreview()!.importKeyCount }) }}</p>
            @if (importPreview()!.added.length) {
              <h4>{{ t('platform.localizationResources.addedKeys') }} ({{ importPreview()!.added.length }})</h4>
              <ul class="diff-list">
                @for (item of importPreview()!.added; track item.key) {
                  <li><code>{{ item.key }}</code> → {{ item.newValue }}</li>
                }
              </ul>
            }
            @if (importPreview()!.changed.length) {
              <h4>{{ t('platform.localizationResources.changedKeys') }} ({{ importPreview()!.changed.length }})</h4>
              <ul class="diff-list">
                @for (item of importPreview()!.changed; track item.key) {
                  <li><code>{{ item.key }}</code>: {{ item.oldValue }} → {{ item.newValue }}</li>
                }
              </ul>
            }
            @if (importPreview()!.removed.length) {
              <h4>{{ t('platform.localizationResources.removedKeys') }} ({{ importPreview()!.removed.length }})</h4>
              <ul class="diff-list">
                @for (item of importPreview()!.removed; track item.key) {
                  <li><code>{{ item.key }}</code> ({{ item.oldValue }})</li>
                }
              </ul>
            }
          }
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="importDialogOpen.set(false)"></button>
          <button
            pButton
            type="button"
            severity="danger"
            [label]="t('platform.localizationResources.confirmReplace')"
            [disabled]="!importPreview()?.isValid || replacing()"
            (click)="confirmReplace()"></button>
        </ng-template>
      }
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); align-items: end; margin-bottom: 12px; }
    .toolbar-actions { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
    label { display: grid; gap: 6px; }
    label span { font-size: 12px; color: #475569; font-weight: 600; }
    .search-field { grid-column: 1 / -1; }
    .meta { color: #64748b; font-size: 13px; margin: 0 0 10px; }
    .value-cell { max-width: 36rem; white-space: pre-wrap; word-break: break-word; }
    .actions-cell, td:last-child { white-space: nowrap; }
    .message { margin-top: 12px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #166534; }
    .dialog-body { display: grid; gap: 12px; }
    .dialog-body textarea { width: 100%; resize: vertical; }
    .diff-list { max-height: 12rem; overflow: auto; margin: 0 0 12px; padding-inline-start: 1.2rem; }
    .hidden-file-input { display: none; }
  `]
})
export class PlatformLocalizationResourceManagerComponent implements OnInit {
  private readonly platformAdmin = inject(PlatformAdminService);
  private readonly localization = inject(LocalizationService);

  readonly catalogFiles = signal<LocalizationResourceFileSummary[]>([]);
  readonly entries = signal<LocalizationResourceEntry[]>([]);
  readonly resourceMode = signal<string | null>(null);
  readonly saving = signal(false);
  readonly replacing = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly editDialogOpen = signal(false);
  readonly importDialogOpen = signal(false);
  readonly editingEntry = signal<LocalizationResourceEntry | null>(null);
  readonly importPreview = signal<LocalizationImportPreviewResponse | null>(null);

  selectedScope = 'admin';
  selectedCulture = 'fa-IR';
  searchTerm = '';
  editValue = '';
  private pendingImportJson = '';

  readonly scopeOptions = computed(() =>
    ['admin', 'organization', 'storefront'].map(value => ({
      value,
      label: this.t(`platform.localizationResources.scopes.${value}`)
    }))
  );

  readonly cultureOptions = computed(() => {
    const cultures = this.catalogFiles()
      .filter(item => item.scope === this.selectedScope && item.exists)
      .map(item => item.culture);
    const unique = [...new Set(cultures)].sort();
    return unique.map(culture => ({ value: culture, label: culture }));
  });

  readonly filteredEntries = computed(() => {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.entries();
    return this.entries().filter(item =>
      item.key.toLowerCase().includes(term) || item.value.toLowerCase().includes(term)
    );
  });

  ngOnInit(): void {
    this.loadCatalog();
  }

  onScopeOrCultureChanged(): void {
    const cultures = this.cultureOptions();
    if (cultures.length && !cultures.some(item => item.value === this.selectedCulture))
      this.selectedCulture = cultures[0].value;
    this.loadEntries();
  }

  loadCatalog(): void {
    this.platformAdmin.listLocalizationResourceFiles().subscribe({
      next: catalog => {
        this.catalogFiles.set(catalog.files);
        if (!catalog.files.some(item => item.scope === this.selectedScope && item.culture === this.selectedCulture && item.exists)) {
          const first = catalog.files.find(item => item.scope === this.selectedScope && item.exists)
            ?? catalog.files.find(item => item.exists);
          if (first) {
            this.selectedScope = first.scope;
            this.selectedCulture = first.culture;
          }
        }
        this.loadEntries();
      },
      error: () => this.errorMessage.set(this.t('platform.localizationResources.loadCatalogFailed'))
    });
  }

  loadEntries(): void {
    if (!this.selectedScope || !this.selectedCulture) return;
    this.errorMessage.set('');
    this.platformAdmin.getLocalizationResourceFile(this.selectedScope, this.selectedCulture).subscribe({
      next: (detail: LocalizationResourceDetailResponse) => {
        this.entries.set(detail.entries);
        this.resourceMode.set(detail.mode);
      },
      error: () => this.errorMessage.set(this.t('platform.localizationResources.loadEntriesFailed'))
    });
  }

  openEditDialog(row: LocalizationResourceEntry): void {
    this.editingEntry.set(row);
    this.editValue = row.value;
    this.editDialogOpen.set(true);
  }

  saveEntry(): void {
    const entry = this.editingEntry();
    if (!entry) return;
    this.saving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.platformAdmin.updateLocalizationResourceEntry(this.selectedScope, this.selectedCulture, entry.key, this.editValue).subscribe({
      next: result => {
        this.saving.set(false);
        this.editDialogOpen.set(false);
        this.successMessage.set(this.t('platform.localizationResources.savedWithBackup', { backup: result.backupPath || this.t('common.none') }));
        this.loadEntries();
        this.refreshShellLocalizationIfNeeded();
      },
      error: () => {
        this.saving.set(false);
        this.errorMessage.set(this.t('common.saveFailed'));
      }
    });
  }

  exportJson(): void {
    this.platformAdmin.exportLocalizationResourceFile(this.selectedScope, this.selectedCulture).subscribe({
      next: blob => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${this.selectedScope}.${this.selectedCulture}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.errorMessage.set(this.t('platform.localizationResources.exportFailed'))
    });
  }

  openImportPicker(): void {
    const input = document.querySelector<HTMLInputElement>('.hidden-file-input');
    input?.click();
  }

  onImportFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      this.pendingImportJson = `${reader.result ?? ''}`;
      this.previewImport();
      input.value = '';
    };
    reader.readAsText(file);
  }

  previewImport(): void {
    this.platformAdmin.previewLocalizationImport(this.selectedScope, this.selectedCulture, this.pendingImportJson).subscribe({
      next: preview => {
        this.importPreview.set(preview);
        this.importDialogOpen.set(true);
      },
      error: () => this.errorMessage.set(this.t('platform.localizationResources.importPreviewFailed'))
    });
  }

  confirmReplace(): void {
    if (!this.pendingImportJson) return;
    this.replacing.set(true);
    this.platformAdmin.replaceLocalizationImport(this.selectedScope, this.selectedCulture, this.pendingImportJson).subscribe({
      next: result => {
        this.replacing.set(false);
        this.importDialogOpen.set(false);
        this.successMessage.set(this.t('platform.localizationResources.replacedWithBackup', { backup: result.backupPath || this.t('common.none') }));
        this.loadCatalog();
        this.loadEntries();
        this.refreshShellLocalizationIfNeeded();
      },
      error: () => {
        this.replacing.set(false);
        this.errorMessage.set(this.t('platform.localizationResources.replaceFailed'));
      }
    });
  }

  private refreshShellLocalizationIfNeeded(): void {
    if (this.selectedScope !== environment.shellScope) return;
    if (this.localization.activeCulture() !== this.selectedCulture) return;
    this.localization.switchCulture(this.selectedCulture);
  }

  t(key: string, params?: Record<string, string | number>): string {
    let value = this.localization.translate(key);
    if (!params) return value;
    for (const [name, replacement] of Object.entries(params))
      value = value.replace(`{{${name}}}`, `${replacement}`);
    return value;
  }
}
